// useTop8 — the band-scoped social-only Top 8 hook, built on useBandQuery/
// useBandMutation so the query key is ALWAYS ['band', bandId, 'top8'] (the tenant in the key, the
// structural fix). The Top 8 is the "MySpace for working bands" ordered grid of up to 8 accepted-friend tiles.
//
// Unlike the base friends routes (friends:manage), top8 is gated on `contacts:write` — the /contacts write
// tier (04). Save PUTs the FULL grid (the server replaces it) and returns the canonical Top8Read, which reseeds
// the cache. The save surfaces the server error verbatim and NEVER reports success on an unpersisted
// write; only ACCEPTED friends are addable and positions are 1..8 (both server-validated).
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type Top8Read = components["schemas"]["Top8Read"];
type Top8Slot = components["schemas"]["Top8Slot"];
type Top8SlotWrite = components["schemas"]["Top8SlotWrite"];

export type { Top8Read, Top8Slot, Top8SlotWrite };

/** The maximum number of Top 8 slots. A fixed UI bound, not a business value.*/
export const TOP8_MAX = 8;

const top8Key = (bandId: string) => ["band", bandId, "top8"] as const;

/** `GET /bands/{bandId}/top8` (contacts:write) — the ordered slots. A 403 surfaces as the query error. */
export function useTop8() {
  const { api } = useApp();
  return useBandQuery<Top8Read>(
    ["top8"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/top8", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

/**
 * `PUT /bands/{bandId}/top8` ({slots:[{position, friendBandId}]}) → Top8Read. The client PUTs the full
 * arranged grid; the server replaces it and returns the canonical order, which reseeds the cache. A 400
 * (non-friend / out-of-range position / duplicate) surfaces verbatim; the card keeps its draft on error.
 */
export function useSaveTop8() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = top8Key(bandId);

  return useBandMutation<Top8Read, Top8SlotWrite[]>(
    async (band, slots) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/top8", {
        params: { path: { bandId: band } },
        body: { slots },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (data) => {
        qc.setQueryData<Top8Read>(key, data);
      },
      onError: (err) => {
        pushToast(errorMessage(err, "Couldn't save your Top 8"));
      },
    },
  );
}
